/* Missing files — invoices completed yesterday/today with no current file in this tool.
 * Union of /missing-files (live file check) and /completed-today (same source as the progress strip). */
import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { API, getAuthHeader } from "@/App";

const LABEL = { packing_list: "Packing List", bulk_data: "Bulk Data", bulk_label: "Bulk Label", pack_team: "Pack Team Print" };

let _mfCache = { at: 0, p: null };
const fetchMf = () => {
  if (!_mfCache.p || Date.now() - _mfCache.at > 5000) {
    _mfCache = { at: Date.now(), p: axios.get(`${API}/order-register/missing-files`, { headers: getAuthHeader() }).then(r => r.data?.tools || {}).catch(() => ({})) };
  }
  return _mfCache.p;
};

export const loadMissing = async (tool) => {
  const h = { headers: getAuthHeader() };
  if (tool === "invoice") {
    return axios.get(`${API}/invoice-doc/missing`, h).then(r => r.data?.invoices || []).catch(() => []);
  }
  const [mf, ct] = await Promise.all([
    fetchMf().then(t => t[tool] || []),
    axios.get(`${API}/order-register/completed-today`, { ...h, params: { scope: "today_yesterday", tool } })
      .then(r => r.data?.invoices || []).catch(() => []),
  ]);
  const out = new Map(mf.map(m => [m.invoice_key, m]));
  for (const i of ct) {
    if (!Number(i.uses_tool?.[tool] || 0) && !out.has(i.invoice_key)) {
      out.set(i.invoice_key, { invoice_key: i.invoice_key, invoice_no: i.invoice_no || i.invoice_key, completed_at: i.completed_at });
    }
  }
  return [...out.values()];
};

export const MissingFilesCard = ({ tool, testId, busy, onUse, refreshKey }) => {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(true);
  useEffect(() => {
    const load = () => loadMissing(tool).then(setList);
    load();
    window.addEventListener("order-register:invoice-changed", load);
    return () => window.removeEventListener("order-register:invoice-changed", load);
  }, [tool, refreshKey]);
  if (!list) return null;
  if (!list.length) return (
    <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-1.5" data-testid={`missing-files-card-${testId}`}>
      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
      <span className="text-xs font-semibold text-emerald-800">Missing {LABEL[tool]} files</span>
      <span className="text-[10px] font-bold bg-emerald-500 text-white rounded-full px-1.5" data-testid={`missing-files-count-${testId}`}>0</span>
      <span className="text-[10px] text-emerald-700">— every invoice completed yesterday / today has a file</span>
    </div>);
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50/70" data-testid={`missing-files-card-${testId}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2 px-3 py-1.5 text-left"
              data-testid={`missing-files-toggle-${testId}`}>
        <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
        <span className="text-xs font-semibold text-amber-900">Missing {LABEL[tool]} files</span>
        <span className="text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5" data-testid={`missing-files-count-${testId}`}>{list.length}</span>
        <span className="text-[10px] text-amber-700">— completed yesterday / today, no file in the table yet</span>
        {open ? <ChevronDown className="w-3.5 h-3.5 ml-auto text-amber-700" /> : <ChevronRight className="w-3.5 h-3.5 ml-auto text-amber-700" />}
      </button>
      {open && (
        <div className="flex flex-wrap gap-1.5 px-3 pb-2">
          {list.map(m => (
            <button key={m.invoice_key} type="button" disabled={busy} onClick={() => onUse(m)}
                    className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-mono font-bold text-amber-800 hover:bg-amber-100 transition-colors"
                    title={`Completed ${m.completed_at ? new Date(m.completed_at).toLocaleString() : ""} — click to create the ${LABEL[tool]} file`}
                    data-testid={`missing-file-chip-${testId}-${m.invoice_key}`}>
              {m.invoice_key}<span className="font-sans font-semibold text-[9px] text-amber-600">Use</span>
            </button>))}
        </div>)}
    </div>
  );
};
